import React from "react";
import { Button, ButtonGroup, Form } from "reactstrap";
import ModalFrame from "../../../toolkit/modalFrame";
import withStyles from "isomorphic-style-loader/withStyles";
import s from "./styles.scss";
import classNames from "classnames";
import {
  executeDonationsExport,
  executeDonationsExportExtended,
  executeDonorsExport,
  executeSegmentDonorsExport,
  executeUsersExport,
  executeDonorsKeywordExport,
  executeConversationsExport,
  executeInvalidDonorsExport,
  executeSmsSummaryExport,
  executeFeesSummaryExport,
  executeSuperAdminReportExport,
  executeSuperadminMetricsAggregationExport,
  executeAdminMetricsAggregationExport,
  executeImportCompatibleDonorsExport,
  DonorActivationFilterParam,
} from "../../../utils/fetch";
import { downloadFile } from "../../../utils/downloadBlobFile";
import { ScaleLoader } from "react-spinners";
import {
  serviceCaller,
  ServiceCallerProps,
  UserCharitiesProps,
  connectUserCharities,
} from "../../composition";
import Fragment from "../../../toolkit/fragment";
import { getParsedDate } from "../../../utils/helpers";

export enum ExportType {
  donors,
  donations,
  segments,
  extendedDonations,
  users,
  keywords,
  conversations,
  invalidDonors,
  smsSummary,
  feesSummary,
  monthlyReport,
  metricsSuperadminAggregation,
  metricsAdminAggregation,
}

interface Props extends ServiceCallerProps, UserCharitiesProps {
  isOpen: boolean;
  toggle: () => void;
  exportType: ExportType;
  exportIdsArray: [string];
  exportTotalIds: string;
  exportFromDate: number;
  exportToDate: number;
  donorId?: string;
  ignoreSelected: boolean;
  exportCharity: string;
  isFlag: boolean;
  donorIds: Array<string>;
  segmentId: string;
  filterBy: string;
  summaryExport?: boolean;
}

interface State {
  csv: boolean;
  loading: boolean;
  donorActivationFilter: DonorActivationFilterParam;
}

class ExportModal extends React.Component<Props, State> {
  state = {
    csv: true,
    loading: false,
    donorActivationFilter: "ACTIVE_ONLY" as DonorActivationFilterParam,
  };

  csvCheck = e => {
    this.setState({
      csv: e.target.checked,
    });
  };

  donorActivationFilterChanged = (value: DonorActivationFilterParam) => {
    this.setState({ donorActivationFilter: value });
  };

  donorActivationScopeLabel = () => {
    switch (this.state.donorActivationFilter) {
      case "DEACTIVATED_ONLY":
        return "deactivated contacts only";
      case "ALL":
        return "all contacts (active and deactivated)";
      default:
        return "active contacts only";
    }
  };

  onImportCompatibleSubmit = () => {
    const proceed = window.confirm(
      `Import-compatible CSV includes ${this.donorActivationScopeLabel()} in your charity. The file matches the import format for re-upload. Continue?`
    );
    if (!proceed) {
      return;
    }
    this.setState({ loading: true });
    const pad = (n: number) => (n < 10 ? "0" : "") + n;
    const d = new Date();
    const exportDate = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
      d.getDate()
    )}`;
    executeImportCompatibleDonorsExport(
      this.props.config.searchServiceUrl,
      this.props.currentCharity.id,
      this.props.serviceAccessToken,
      exportDate,
      this.state.donorActivationFilter
    )
      .then(({ blob, filename }) => {
        downloadFile(blob, filename);
        this.setState({ loading: false });
        this.props.toggle();
      })
      .catch(err => {
        const msg =
          err && typeof (err as Error).message === "string"
            ? (err as Error).message
            : "Import-compatible export failed.";
        alert(msg);
        this.setState({ loading: false });
      });
  };

  onSubmit = () => {
    if (this.props.exportType === ExportType.donations) {
      this.setState({ loading: true });
      executeDonationsExport(
        this.props.config.searchServiceUrl,
        this.props.currentCharity.id,
        this.props.exportFromDate,
        this.props.exportToDate,
        this.props.exportIdsArray,
        this.props.ignoreSelected,
        this.props.donorId,
        this.props.serviceAccessToken
      ).then(blob => {
        downloadFile(blob, "donations.csv");
        this.setState({ loading: false });
        this.props.toggle();
      });
    } else if (this.props.exportType === ExportType.donors) {
      this.setState({ loading: true });
      executeDonorsExport(
        this.props.config.searchServiceUrl,
        this.props.currentCharity.id,
        this.props.exportIdsArray,
        this.props.ignoreSelected,
        this.props.serviceAccessToken,
        this.props.filterBy,
        this.props.exportFromDate,
        this.props.exportToDate,
        this.state.donorActivationFilter
      ).then(blob => {
        downloadFile(blob, "donors.csv");
        this.setState({ loading: false });
        this.props.toggle();
      });
    } else if (this.props.exportType === ExportType.users) {
      this.setState({ loading: true });
      executeUsersExport(
        this.props.config.searchServiceUrl,
        this.props.currentCharity.id,
        this.props.exportIdsArray,
        this.props.ignoreSelected,
        this.props.serviceAccessToken
      ).then(blob => {
        downloadFile(blob, "users.csv");
        this.setState({ loading: false });
        this.props.toggle();
      });
    } else if (this.props.exportType === ExportType.segments) {
      this.setState({ loading: true });
      executeSegmentDonorsExport(
        this.props.config.searchServiceUrl,
        this.props.currentCharity.id,
        this.props.segmentId,
        this.props.isFlag,
        this.props.serviceAccessToken,
        this.props.donorIds
      ).then(blob => {
        downloadFile(blob, "donorsForSegment.csv");
        this.setState({ loading: false });
        this.props.toggle();
      });
    } else if (this.props.exportType === ExportType.extendedDonations) {
      this.setState({ loading: true });
      executeDonationsExportExtended(
        this.props.config.searchServiceUrl,
        this.props.exportCharity,
        this.props.exportFromDate,
        this.props.exportToDate,
        this.props.exportIdsArray,
        this.props.ignoreSelected,
        this.props.serviceAccessToken
      ).then(blob => {
        downloadFile(blob, "donations.csv");
        this.setState({ loading: false });
        this.props.toggle();
      });
    } else if (this.props.exportType === ExportType.keywords) {
      this.setState({ loading: true });
      executeDonorsKeywordExport(
        this.props.config.adminServiceUrl,
        this.props.currentCharity.id,
        this.props.segmentId,
        this.props.donorIds,
        this.props.serviceAccessToken
      )
        .then(blob => {
          downloadFile(
            blob,
            "donorsKeyword-" + this.props.exportCharity + ".csv"
          );
          this.setState({ loading: false });
          this.props.toggle();
        })
        .catch(error => {
          alert(error);
          this.setState({
            loading: false,
          });
          this.props.toggle();
        });
    } else if (this.props.exportType === ExportType.conversations) {
      this.setState({ loading: true });
      executeConversationsExport(
        this.props.config.notificationServiceUrl,
        this.props.currentCharity.id,
        this.props.serviceAccessToken
      ).then(blob => {
        downloadFile(blob, "conversations.csv");
        this.setState({ loading: false });
        this.props.toggle();
      });
    } else if (this.props.exportType === ExportType.invalidDonors) {
      this.setState({ loading: true });
      executeInvalidDonorsExport(
        this.props.config.searchServiceUrl,
        this.props.currentCharity.id,
        this.props.serviceAccessToken
      ).then(blob => {
        downloadFile(
          blob,
          `Invalid Contact Report ${getParsedDate(
            new Date().toString(),
            false,
            "MM-DD-YYYY"
          )}.csv`
        );
        this.setState({ loading: false });
        this.props.toggle();
      });
    } else if (
      this.props.exportType === ExportType.metricsSuperadminAggregation
    ) {
      this.setState({ loading: true });
      executeSuperadminMetricsAggregationExport(
        this.props.config.searchServiceUrl,
        this.props.exportIdsArray,
        this.props.exportFromDate,
        this.props.exportToDate,
        this.props.serviceAccessToken
      ).then(blob => {
        downloadFile(blob, "metricsAggregation.csv");
        this.setState({ loading: false });
      });
    } else if (this.props.exportType === ExportType.metricsAdminAggregation) {
      this.setState({ loading: true });
      executeAdminMetricsAggregationExport(
        this.props.config.searchServiceUrl,
        this.props.exportIdsArray[0],
        this.props.exportFromDate,
        this.props.exportToDate,
        this.props.serviceAccessToken
      ).then(blob => {
        downloadFile(blob, "metricsAggregation.csv");
        this.setState({ loading: false });
      });
    } else {
      this.props.toggle();
    }
  };

  onSubmitCurrentMonth = () => {
    let date = new Date();
    let fromDate = new Date(date.getFullYear(), date.getMonth(), 1).getTime();
    let to = date.getTime();
    if (this.props.exportType === ExportType.smsSummary) {
      this.setState({ loading: true });
      executeSmsSummaryExport(
        this.props.config.searchServiceUrl,
        this.props.serviceAccessToken,
        this.props.charities,
        fromDate,
        to
      ).then(blob => {
        downloadFile(blob, "smsSummaryCurrentMonth.csv");
        this.setState({ loading: false });
        this.props.toggle();
      });
    } else if (this.props.exportType === ExportType.feesSummary) {
      this.setState({ loading: true });
      executeFeesSummaryExport(
        this.props.config.searchServiceUrl,
        this.props.serviceAccessToken,
        this.props.charities,
        fromDate,
        to
      ).then(blob => {
        downloadFile(blob, "feesSummaryCurrentMonth.csv");
        this.setState({ loading: false });
        this.props.toggle();
      });
    } else if (this.props.exportType === ExportType.monthlyReport) {
      this.setState({ loading: true });
      executeSuperAdminReportExport(
        this.props.config.searchServiceUrl,
        this.props.serviceAccessToken,
        this.props.charities,
        fromDate,
        to
      ).then(blob => {
        downloadFile(blob, "MonthlyReportCurrentMonth.csv");
        this.setState({ loading: false });
        this.props.toggle();
      });
    }
  };

  onSubmitPastMonth = () => {
    let date = new Date();
    let fromDate;
    let to;
    if (date.getMonth() === 0) {
      fromDate = new Date(date.getFullYear() - 1, 11, 1).getTime();
      to = new Date(date.getFullYear() - 1, 11, 31).getTime() + 86400000;
    } else {
      fromDate = new Date(date.getFullYear(), date.getMonth() - 1, 1).getTime();
      to =
        new Date(date.getFullYear(), date.getMonth(), 0).getTime() + 86400000;
    }
    if (this.props.exportType === ExportType.smsSummary) {
      this.setState({ loading: true });
      executeSmsSummaryExport(
        this.props.config.searchServiceUrl,
        this.props.serviceAccessToken,
        this.props.charities,
        fromDate,
        to
      ).then(blob => {
        downloadFile(blob, "smsSummaryLastMonth.csv");
        this.setState({ loading: false });
        this.props.toggle();
      });
    } else if (this.props.exportType === ExportType.feesSummary) {
      this.setState({ loading: true });
      executeFeesSummaryExport(
        this.props.config.searchServiceUrl,
        this.props.serviceAccessToken,
        this.props.charities,
        fromDate,
        to
      ).then(blob => {
        downloadFile(blob, "feesSummaryLastMonth.csv");
        this.setState({ loading: false });
        this.props.toggle();
      });
    } else if (this.props.exportType === ExportType.monthlyReport) {
      this.setState({ loading: true });
      executeSuperAdminReportExport(
        this.props.config.searchServiceUrl,
        this.props.serviceAccessToken,
        this.props.charities,
        fromDate,
        to
      ).then(blob => {
        downloadFile(blob, "MonthlyReportLastMonth.csv");
        this.setState({ loading: false });
        this.props.toggle();
      });
    }
  };

  getHeaderSubtitle = () => {
    if (this.props.exportType === ExportType.conversations) {
      return "All Conversations";
    }
    if (this.props.exportType === ExportType.invalidDonors) {
      return `${this.props.exportTotalIds} Contacts Currently Invalid`;
    }
    if (this.props.exportType === ExportType.smsSummary) {
      return "Sms Summary";
    }
    if (this.props.exportType === ExportType.feesSummary) {
      return "All Donation Fees";
    }
    if (this.props.exportType === ExportType.monthlyReport) {
      return "Monthly Report";
    }
    if (this.props.exportType === ExportType.metricsAdminAggregation) {
      return "Charity Metrics";
    }
    return `${!this.props.exportTotalIds ? 0 : this.props.exportTotalIds}
        ${
          this.props.exportType == ExportType.donors
            ? "Donor"
            : this.props.exportType == ExportType.donations
            ? "Donation"
            : this.props.exportType == ExportType.metricsSuperadminAggregation
            ? "Charities"
            : "Users"
        } Records`;
  };

  render() {
    return (
      <ModalFrame
        isOpen={this.props.isOpen}
        toggle={() => this.props.toggle()}
        size="md"
        title={"Export"}
        subtitle={this.getHeaderSubtitle()}
      >
        <div className={s.centered}>
          <ScaleLoader color={"#1f65b1"} loading={this.state.loading} />
        </div>
        {!this.state.loading && (
          <div>
            {this.props.exportType === ExportType.invalidDonors && (
              <Fragment>
                <p className={s.label}>
                  Exports contacts whose phone or email is currently marked
                  invalid, the same state shown in Contacts. It is not a history
                  of delivery failures.
                </p>
                <br />
              </Fragment>
            )}
            <p className={s.label}>Select File Type</p>
            <Form>
              <div>
                <input
                  id="csv"
                  name="csv"
                  type="checkbox"
                  disabled={true}
                  checked={this.state.csv}
                  onChange={this.csvCheck.bind(this)}
                />
                <label htmlFor="csv">
                  <span className={s.label + " " + s.csvLabel}>CSV</span>
                </label>
              </div>
              {!this.props.summaryExport && (
                <Fragment>
                  {this.props.exportType === ExportType.donors && (
                    <Fragment>
                      <p className={classNames(s.label, s.activationFilterLabel)}>
                        Contacts to export
                      </p>
                      <ButtonGroup className={s.activationFilterGroup}>
                        <Button
                          className={classNames(
                            s.activationFilterButton,
                            this.state.donorActivationFilter === "ALL"
                              ? s.activationFilterButtonActive
                              : undefined
                          )}
                          active={this.state.donorActivationFilter === "ALL"}
                          onClick={() => this.donorActivationFilterChanged("ALL")}
                          color={"none"}
                        >
                          All
                        </Button>
                        <Button
                          className={classNames(
                            s.activationFilterButton,
                            this.state.donorActivationFilter === "ACTIVE_ONLY"
                              ? s.activationFilterButtonActive
                              : undefined
                          )}
                          active={
                            this.state.donorActivationFilter === "ACTIVE_ONLY"
                          }
                          onClick={() =>
                            this.donorActivationFilterChanged("ACTIVE_ONLY")
                          }
                          color={"none"}
                        >
                          Active
                        </Button>
                        <Button
                          className={classNames(
                            s.activationFilterButton,
                            this.state.donorActivationFilter ===
                              "DEACTIVATED_ONLY"
                              ? s.activationFilterButtonActive
                              : undefined
                          )}
                          active={
                            this.state.donorActivationFilter ===
                            "DEACTIVATED_ONLY"
                          }
                          onClick={() =>
                            this.donorActivationFilterChanged(
                              "DEACTIVATED_ONLY"
                            )
                          }
                          color={"none"}
                        >
                          Deactivated
                        </Button>
                      </ButtonGroup>
                    </Fragment>
                  )}
                  <Button
                    onClick={this.onSubmit}
                    className={classNames(s.button, s.roundedCorners)}
                  >
                    Export
                  </Button>
                  {this.props.exportType === ExportType.donors && (
                    <Button
                      type="button"
                      outline
                      color="secondary"
                      onClick={this.onImportCompatibleSubmit}
                      className={classNames(
                        s.buttonSecondary,
                        s.roundedCorners
                      )}
                    >
                      Import-compatible CSV
                    </Button>
                  )}
                </Fragment>
              )}
              {this.props.summaryExport && (
                <Fragment>
                  <Button
                    onClick={this.onSubmitPastMonth}
                    className={classNames(s.button, s.roundedCorners)}
                  >
                    Last Month
                  </Button>
                  <Button
                    onClick={this.onSubmitCurrentMonth}
                    className={classNames(s.button, s.roundedCorners)}
                  >
                    Current Month
                  </Button>
                </Fragment>
              )}
            </Form>
          </div>
        )}
      </ModalFrame>
    );
  }
}

export default serviceCaller(connectUserCharities(withStyles(s)(ExportModal)));
